import { useMemo } from 'react'
import { api } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  FilterResult,
  FilterStage,
  MomentumRow,
  SignalsSnapshot,
  UniverseSnapshot,
} from '../api/types'
import { Badge } from '../components/ui/Badge'
import { ExternalLink } from '../components/ui/ExternalLink'
import { Panel } from '../components/ui/Panel'
import { EmptyState, QueryView } from '../components/ui/States'
import { CHAIN_HISTORY_START, explorerTokenUrl } from '../config'
import { fmtNum, fmtTime } from '../lib/format'
import { buildTokenIndex, nameFor, symbolFor } from '../lib/tokens'

/**
 * Engine filter ladder (engine/src/filters.ts): stages short-circuit, so a
 * failing token records its FIRST failing stage; later stages are not
 * evaluated that cycle.
 */
const STAGES: { stage: FilterStage; label: string; title: string }[] = [
  { stage: 1, label: 'hist', title: 'history: enough trading days + weekday coverage (adaptive to chain age)' },
  { stage: 2, label: 'vol', title: 'volume: median 7-day and 24h USD volume above floors' },
  { stage: 3, label: 'liq', title: 'liquidity: measured RFQ/AMM impact within bounds (or volume-only admit, flagged unverified)' },
  { stage: 4, label: 'fresh', title: 'staleness: recent trades, few zero-volume days, closes not flat' },
  { stage: 5, label: 'sane', title: 'anomaly: no absurd daily return or intraday range' },
]

export function Signals() {
  const signals = useQuery(() => api.signalsLatest())
  const universe = useQuery(() => api.universeLatest())

  const snapshot: UniverseSnapshot | null =
    universe.state.kind === 'ready' ? universe.state.data.snapshot : null

  return (
    <div className="space-y-6">
      <Panel title="universe // filter ladder + momentum">
        <QueryView state={signals.state} onRetry={signals.refresh}>
          {(s) =>
            s.signals ? (
              <UniverseTable signals={s.signals} universe={snapshot} />
            ) : (
              <EmptyState
                title="no signals yet"
                detail="No signal cycle has run yet. Signals appear after the first daily universe + filter pass."
              />
            )
          }
        </QueryView>
      </Panel>

      <Panel title="reading this table">
        <ul className="list-inside space-y-1 text-xs text-crt-dim">
          <li>
            &gt; the ladder short-circuits: a failing token shows its first
            failing stage; later stages are unevaluated (dim) that cycle.
          </li>
          <li>
            &gt; S is the cross-sectional momentum score (volatility-adjusted
            return blend + volume confirmation, z-scored over gate-passing
            tokens). Bars are scaled to the largest |S| currently on screen.
            A dash means the trend/RSI gate failed.
          </li>
          <li>
            &gt; windows are adaptive: the chain is weeks old (history begins{' '}
            {CHAIN_HISTORY_START}), so lookbacks shrink to the data actually
            available. Scores over short windows are noisy — treat rankings
            as provisional.
          </li>
        </ul>
      </Panel>
    </div>
  )
}

function UniverseTable({
  signals,
  universe,
}: {
  signals: SignalsSnapshot
  universe: UniverseSnapshot | null
}) {
  const index = useMemo(() => buildTokenIndex(universe), [universe])
  const momentumByAddr = useMemo(() => {
    const m = new Map<string, MomentumRow>()
    for (const row of signals.momentum) m.set(row.address.toLowerCase(), row)
    return m
  }, [signals])
  const maxAbsScore = useMemo(() => {
    let m = 0
    for (const r of signals.momentum) {
      if (r.S !== null) m = Math.max(m, Math.abs(r.S))
    }
    return m
  }, [signals])

  if (signals.filters.length === 0) {
    return (
      <EmptyState
        title="universe empty"
        detail="No tokens have been screened yet. The universe fills after the first indexer + filter cycle."
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] text-sm">
        <thead>
          <tr>
            <th className="th">token</th>
            <th className="th">filter stages</th>
            <th className="th">eligible</th>
            <th className="th">momentum S</th>
            <th className="th text-right">rsi</th>
            <th className="th text-right">days</th>
            <th className="th">gate</th>
          </tr>
        </thead>
        <tbody>
          {signals.filters.map((row) => {
            const mom = momentumByAddr.get(row.address.toLowerCase())
            return (
              <tr key={row.address} className="hover:bg-crt-panel2">
                <td className="td">
                  <ExternalLink href={explorerTokenUrl(row.address)}>
                    {symbolFor(index, row.address)}
                  </ExternalLink>
                  <div className="text-[11px] text-crt-dim">
                    {nameFor(index, row.address)}
                  </div>
                </td>
                <td className="td">
                  <StageBadges result={row} />
                </td>
                <td className="td">
                  <Badge tone={row.pass ? 'pos' : 'neg'}>
                    {row.pass ? 'pass' : 'fail'}
                  </Badge>
                </td>
                <td className="td">
                  <ScoreBar score={mom?.S ?? null} maxAbs={maxAbsScore} />
                </td>
                <td className="td text-right text-xs">
                  {mom && mom.rsi !== null ? fmtNum(mom.rsi, 1) : '—'}
                </td>
                <td className="td text-right text-xs text-crt-dim">
                  {mom ? mom.n : row.metrics.tradingDayCount}
                </td>
                <td className="td">
                  {mom ? (
                    <Badge
                      tone={mom.gate ? 'pos' : 'neg'}
                      title="momentum gate: price above moving averages, RSI in band"
                    >
                      {mom.gate ? 'open' : 'shut'}
                    </Badge>
                  ) : (
                    <Badge tone="dim" title="Momentum is only scored for tokens that pass the filter ladder.">
                      n/a
                    </Badge>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-crt-dim">
        as of {fmtTime(signals.asOf)}
        {typeof signals.meta?.['chainAgeDays'] === 'number'
          ? ` // chain age ${String(signals.meta['chainAgeDays'])}d`
          : ''}
      </div>
    </div>
  )
}

function StageBadges({ result }: { result: FilterResult }) {
  const failedAt = result.pass ? null : (result.stage_failed ?? null)
  const reasons =
    result.reasons.length > 0 ? result.reasons.join('; ') : 'no reason recorded'
  return (
    <div className="flex flex-wrap gap-1">
      {STAGES.map(({ stage, label, title }) => {
        let tone: 'pos' | 'neg' | 'dim' = 'pos'
        let t = title
        if (failedAt !== null) {
          if (stage === failedAt) {
            tone = 'neg'
            t = `${title} — FAILED: ${reasons}`
          } else if (stage > failedAt) {
            tone = 'dim'
            t = `${title} — not evaluated (ladder short-circuited at stage ${failedAt})`
          }
        }
        return (
          <Badge key={stage} tone={tone} title={t}>
            {label}
          </Badge>
        )
      })}
      {result.flags.liquidity_unverified ? (
        <Badge
          tone="orange"
          title="Admitted on volume alone — liquidity unverified; the engine halves this token's position cap."
        >
          liq?
        </Badge>
      ) : null}
    </div>
  )
}

/** Horizontal signed S-score bar, scaled to the max |S| on screen. */
function ScoreBar({
  score,
  maxAbs,
}: {
  score: number | null
  maxAbs: number
}) {
  if (score === null) {
    return (
      <span
        className="text-xs text-crt-dim"
        title="No score: token failed the filter ladder or its trend/RSI gate."
      >
        —
      </span>
    )
  }
  const denom = maxAbs > 0 ? maxAbs : 1
  const frac = Math.min(1, Math.abs(score) / denom)
  return (
    <div className="flex items-center gap-2">
      <div
        className="relative h-3 w-32 border border-crt-dim/60"
        title={`S = ${score.toFixed(3)}`}
      >
        <div className="absolute left-1/2 top-0 h-full w-px bg-crt-dim" />
        {score >= 0 ? (
          <div
            className="absolute left-1/2 top-0 h-full bg-crt-lime/80"
            style={{ width: `${frac * 50}%` }}
          />
        ) : (
          <div
            className="absolute top-0 h-full bg-crt-neg/70"
            style={{ right: '50%', width: `${frac * 50}%` }}
          />
        )}
      </div>
      <span
        className={`text-xs ${score >= 0 ? 'text-crt-lime' : 'text-crt-neg'}`}
      >
        {score.toFixed(2)}
      </span>
    </div>
  )
}
